import * as R from "ramda";

import { getMd5OfFile as getMd5OfFileUtil } from "./upload-resource-util";

import Dexie from "dexie/dist/dexie.js";

// noinspection NonAsciiCharacters
/**
 * 数据表设计
 * ++id: 主键，自增 1,2,3...
 * orderId: 订单id => 1
 * sha: 文件sha256 => 39327d9481ecff91450177836bfd3166fb2302eeb020f8899165928ed600e612
 * name: 文件名 => 1.mp4
 * size: 文件大小 => 1024
 * path: 文件路径 => /a/b/c/1.mp4
 * type: 文件类型 => video|audio|image|text
 * status: 文件状态 => 0: 未上传 1: 上传中 2: 上传失败 3: 上传成功
 * upload_date: 上传时间 => 2021-01-01 12:00:00
 * last_modified: 最后修改时间 => 2021-01-01 12:00:00
 */

export const UploadStatusMap = {
  未上传: 0,
  上传中: 1,
  上传失败: 2,
  上传成功: 3,
};

export const UploadTextMap = R.invertObj(UploadStatusMap);

// 创建数据库
const db = new Dexie("VideoResourceDB");

// 定义表结构
db.version(1).stores({
  VideoResourceUploadFile:
    "++id, orderId, sha, name, size, path, type, status, upload_date, last_modified",
});

// 获取表
const videoResourceTable = db.table("VideoResourceUploadFile");

// 查询文件
export const queryFile = async (file, orderId) => {
  const sha = await getMd5OfFileUtil(file);
  return videoResourceTable.get({ orderId, sha, path: file.path });
};

// 修改文件状态
export const updateFileStatus = async (file, orderId, status) => {
  const sha = await getMd5OfFileUtil(file);
  await videoResourceTable
    .where({ orderId, sha, path: file.path })
    .modify({ status, last_modified: new Date() });
};

// 更新文件列表
export const updateFileList = async (files, orderId) => {
  const fileUpdates = files.map(async (file) => {
    const sha = await getMd5OfFileUtil(file);
    const existingFile = await videoResourceTable.get({
      orderId,
      sha,
      path: file.path,
    });
    if (existingFile) {
      await videoResourceTable.update(existingFile.id, {
        last_modified: new Date(),
      });
    } else {
      await videoResourceTable.add({
        orderId,
        sha,
        name: file.name,
        size: file.size,
        path: file.path,
        type: file.type,
        status: 0, // 未上传
        upload_date: new Date(),
        last_modified: new Date(),
      });
    }
  });
  await Promise.all(fileUpdates);
};

// 删除文件
export const deleteFile = async (file, orderId) => {
  const sha = await getMd5OfFileUtil(file);
  await videoResourceTable.where({ orderId, sha, path: file.path }).delete();
};

// 获取所有文件
export const getAllFiles = (orderId) => {
  return videoResourceTable.where({ orderId }).toArray();
};

// 获取订单下面的一个文件, status 为未上传
export const getOneFile = (orderId) => {
  return videoResourceTable
    .where({ orderId, status: UploadStatusMap.未上传 })
    //  order by id asc
    .sortBy("id")
    .then((files) => files[0]);
};

/**
 * 每次获取到文件夹下的数据后
 * 1. 如果没有, 则插入, 默认状态为未上传
 * 2. 如果有, 则不做更新
 *
 * @param files
 * @param orderId
 * @param pathList
 * @returns {Promise<void>}
 */
export const insertOrUpdateFile = async (orderId, files, pathList) => {
  return files.map(async (file, index) => {
    const realFile = await file.getFile();
    const sha = await getMd5OfFileUtil(realFile);
    const existingFile = await videoResourceTable.get({
      orderId,
      sha,
      path: pathList[index],
    });
    if (!existingFile) {
      await videoResourceTable.add({
        orderId,
        sha,
        name: realFile.name,
        size: realFile.size,
        path: pathList[index],
        type: realFile.type,
        status: UploadStatusMap.未上传,
        upload_date: new Date(),
        last_modified: new Date(),
      });
    }
  });
};


export const getOrderListByMd5List = (orderId, md5List = []) => {
  return Promise.all(
    md5List.map(async (sha) => {
      return videoResourceTable.where({ orderId: Number(orderId), sha }).toArray();
    })
  );
}

//  当一个文件上传成功后, 修改状态, 根据其id即可
export const updateFileStatusById = async (id, status) => {
  await videoResourceTable.update(id, { status, upload_date: new Date() });
};

export const getFilesByRealFiles = (orderId, realFiles = [], paths = []) => {
  return Promise.all(
    realFiles.map(async (file, index) => {
      const sha = await getMd5OfFileUtil(file);
      return videoResourceTable.get({ orderId, sha, path: paths[index] });
    })
  );
};



// WEBPACK FOOTER //
// ./src/containers/Video/widgets/indexed-db-manipulation.js